import { message } from 'antd';
import React, { useCallback, useMemo, useRef } from 'react';
import ReactQuill from 'react-quill-new';
import './styles.modules.scss'

interface CustomRichTextProps {
  value?: string;
  onChange?: (content: string) => void;
}

const MAX_IMAGE_SIZE_IN_BYTES = 5 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];

export const CustomRichText: React.FunctionComponent<CustomRichTextProps> = ({ value, onChange }) => {
  const quillRef = useRef<ReactQuill | null>(null);

  const handleImage = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', ALLOWED_IMAGE_TYPES.join(','));
    input.click();

    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) {
        return;
      }

      if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
        message.error('Alleen PNG, JPG, JPEG, WebP en GIF afbeeldingen zijn toegestaan');
        return;
      }

      if (file.size > MAX_IMAGE_SIZE_IN_BYTES) {
        message.error('Afbeeldingen mogen maximaal 5 MB zijn');
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result !== 'string') {
          return;
        }

        const editor = quillRef.current?.getEditor();
        if (!editor) {
          return;
        }

        const range = editor.getSelection(true);
        const insertIndex = range?.index ?? editor.getLength();
        editor.insertEmbed(insertIndex, 'image', reader.result);
        editor.setSelection(insertIndex + 1, 0);
      };
      reader.readAsDataURL(file);
    };
  }, []);

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: '1' }, { header: '2' }],
          [{ font: [] }],
          [{ size: ['small', false, 'large', 'huge'] }],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ align: [] }],
          ['bold', 'italic', 'underline'],
          ['link', 'image'],
          ['blockquote', 'code-block'],
          [{ indent: '-1' }, { indent: '+1' }],
          [{ direction: 'rtl' }],
          ['clean'],
        ],
        handlers: {
          image: handleImage,
        },
      },
    }),
    [handleImage]
  );

  return (
    <ReactQuill
      ref={quillRef}
      value={value}
      onChange={onChange}
      modules={modules}
      formats={[
        'header', 'font', 'size', 'list', 'align', 'bold', 'italic', 'underline',
        'link', 'image', 'blockquote', 'code-block', 'indent', 'direction', 'clean',
      ]}
      style={{ width: '800px', height: 'auto', marginBottom: '20px', backgroundColor: 'white', fontStyle: 'Times New Roman' }}
    />
  );
};
